/**
 * @fileoverview The `sourceGenre` filter-key ↔ display-label mapping (WEB-832).
 *
 * `Event.sourceGenre` (PIP-234) carries **the source's own display casing** —
 * `"Rock"`, `"Hip-Hop/Rap"`, `"R&B"` — deliberately, because it is a value the
 * source actually published and re-casing it would be inventing a label. That
 * makes it unlike `Event.sourceCategory`, which is stored already-lowercased
 * and therefore doubles as its own URL filter key.
 *
 * So genre needs two strings where category needs one:
 *
 *   - the **label**, `"Hip-Hop/Rap"` — what the source published, what the chip
 *     and the active-filter strip render, never re-derived or title-cased by us.
 *   - the **key**, `"hip-hop-rap"` — a URL-safe, casing-stable slug, what
 *     `?genre=` carries and what the RTK Query cache key hashes.
 *
 * The key is derived FROM the label by {@link genreFilterKey}, and never the
 * other way around: the transform is lossy (`"Hip-Hop/Rap"` and `"Hip Hop Rap"`
 * both key to `hip-hop-rap`), so a key can only be turned back into a label by
 * looking it up against genres that actually exist in the catalogue. The server
 * does exactly that (`resolveGenreFilterLabels` in `discoverFacets.ts`) rather
 * than reconstructing a label from the slug; the client reads labels off the
 * facet payload for the same reason. Nothing anywhere un-slugifies.
 *
 * Deriving the key rather than storing a second column is what keeps this
 * honest across a source-side rename: the day Ticketmaster ships a new genre,
 * it gets a key without a migration, and no row carries a slug we invented.
 *
 * @since WEB-832 (Discover · genre filter/facet)
 */

/**
 * URL/query param name carrying the active genre filter (keys, comma-joined).
 */
export const GENRE_QUERY_PARAM = 'genre';

/**
 * Maximum length of a genre filter key. Generous relative to any real
 * source genre (`"Dance/Electronic"` keys to 16 chars) and short enough that a
 * crafted URL cannot use the param as a payload carrier.
 */
export const GENRE_FILTER_KEY_MAX_LENGTH = 64;

/**
 * Derive the URL filter key for a source-published genre label.
 *
 * Lowercases, collapses every run of non-alphanumeric characters into a single
 * `-`, and trims leading/trailing `-`. Deterministic and shared by the producer
 * (the facet aggregation, which emits the key alongside the label) and the
 * consumer (URL-state sanitisation), so the two can never disagree about what
 * `"Hip-Hop/Rap"` is called in a link.
 *
 * Returns `''` for a label with no alphanumeric content — callers MUST drop
 * such a genre rather than emitting a keyless facet bucket.
 *
 * @example genreFilterKey('Hip-Hop/Rap') // 'hip-hop-rap'
 * @example genreFilterKey('R&B')         // 'r-b'
 * @example genreFilterKey('Rock')        // 'rock'
 */
export function genreFilterKey(label: string): string {
  const collapsed = label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  // Trim with index arithmetic rather than `.replace(/^-+|-+$/g, '')`. CodeQL
  // flags that pattern as `js/polynomial-redos` (alert #828) on the grounds
  // that `-+$` retries from every index, so it is O(n^2) on a long run of `-`,
  // and this function is exported from `@loci/types` — to CodeQL its parameter
  // is uncontrolled library input.
  //
  // A run cannot actually reach the trim: the collapse above rewrites every
  // maximal non-alphanumeric run as exactly ONE `-`, so the trim never sees
  // `--` and each `-+` could only ever match a single character. That makes
  // the alert a false positive TODAY — but only because of an unstated
  // invariant of the line above, which a later edit to the collapse would
  // silently break. A linear trim costs nothing, holds regardless of what the
  // collapse does, and removes a high-severity alert rather than carrying it.
  // Same reasoning as `isGenreFilterKey` below, which hand-avoids its own
  // ReDoS for a genuinely reachable input.
  let start = 0;
  let end = collapsed.length;
  while (start < end && collapsed[start] === '-') start += 1;
  while (end > start && collapsed[end - 1] === '-') end -= 1;

  return collapsed.slice(start, end);
}

/**
 * Whether a raw URL value is shaped like a genre filter key.
 *
 * Used to sanitise `?genre=` on parse/serialize so a crafted or stale value
 * never round-trips into the backend filter — the same guard `normalizeVibes`
 * applies to `?vibe=`, minus the closed vocabulary (genres are open-ended:
 * whatever the source published). A value that fails this is dropped silently,
 * never thrown on, so a stale bookmark degrades to "no genre filter" instead of
 * a 400.
 *
 * Note this validates SHAPE only. A well-shaped key for a genre no event
 * carries resolves to zero labels server-side and correctly returns an empty
 * result — it is a filter that matches nothing, not a filter that is ignored.
 */
export function isGenreFilterKey(value: string): boolean {
  if (value.length === 0 || value.length > GENRE_FILTER_KEY_MAX_LENGTH) return false;
  // Deliberately NOT `/^[a-z0-9]+(?:-[a-z0-9]+)*$/`. That pattern nests two
  // ambiguous quantifiers, so a near-miss input (`'a-a-a-…-a!'`) backtracks
  // polynomially — CodeQL flags it as a ReDoS on uncontrolled data, and since
  // WEB-832 wired this into `discoverGenreArraySchema` the input IS
  // uncontrolled: `/events/discover` is a public GET. The length cap above
  // bounds the blast radius, but a linear check costs nothing and removes it.
  //
  // The three conditions below are exactly equivalent to that pattern:
  // alphanumeric segments joined by single dashes, with no empty segment.
  if (!/^[a-z0-9-]+$/.test(value)) return false;
  if (value.startsWith('-') || value.endsWith('-')) return false;
  return !value.includes('--');
}

/**
 * Filter keys for source-published labels that mean "no genre" rather than
 * naming one. Keyed (not raw) so a punctuation variant cannot evade the rule:
 * these are compared against {@link genreFilterKey} output, the same identity
 * the facet buckets by and the URL carries.
 */
export const PLACEHOLDER_GENRE_FILTER_KEYS: ReadonlySet<string> = new Set(['other', 'undefined']);

/**
 * Whether a genre filter key stands for a placeholder rather than a genre.
 *
 * Ticketmaster publishes two of these. `"Undefined"` is its explicit
 * not-classified-this-finely sentinel, already dropped at ingest by
 * `normalizeSourceClassificationValue` (PIP-234) — this is the read-side
 * backstop for a lane that does not route through it. `"Other"` is different
 * and is why this function exists: it is a real name in TM's vocabulary (the
 * `Miscellaneous` segment's catch-all genre), so PIP-234's honest-data contract
 * correctly STORES it. Storing it is right; offering it as a filter is not.
 *
 * A chip labelled "Other" would mean "events whose genre is the absence of a
 * genre". Clicking it hides every event that has a real genre — the exact
 * defect a NULL bucket has, arrived at through a value the source did publish.
 * So the decision belongs here, at the filter boundary, not at ingest.
 *
 * Applied at BOTH ends of the round trip — the facet never emits such a bucket,
 * and `buildGenreLookup` never resolves such a key — so a hand-written
 * `?genre=other` is an unknown key returning the empty set, not a hidden filter
 * no chip offers.
 */
export function isPlaceholderGenreKey(key: string): boolean {
  return PLACEHOLDER_GENRE_FILTER_KEYS.has(key);
}
